import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import SpaceHeader from './SpaceHeader';
import { VscMarkdown, VscChromeClose } from 'react-icons/vsc';
import { cn } from '@/utils/utils';
import { useTheme } from '@/components/ui/theme-provider';
import { Editor } from "novel-lightweight";

// Define the Page interface for tabs
interface Page {
  index: number;
  name: string;
  route: string;
  visible: boolean;
  mode: 'view' | 'edit';
  content: React.ReactNode;
}

// Initial pages for the studio space
const initialPages: Page[] = [
  { 
    index: 0, 
    name: 'welcome.md', 
    route: '/studio/welcome', 
    visible: true,
    mode: 'view',
    content: (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Welcome to Studio Space</h1>
        <p className="mb-4">This is where you can create and manage your content.</p>
        <p>Use the tabs above to navigate between different pages.</p>
      </div>
    )
  },
  { 
    index: 1, 
    name: 'getting-started.md', 
    route: '/studio/getting-started', 
    visible: true,
    mode: 'view',
    content: (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Getting Started</h1>
        <p className="mb-4">Follow these steps to get started with Studio Space:</p>
        <ol className="list-decimal pl-6 mb-4">
          <li className="mb-2">Create a new document by clicking the "+" button</li>
          <li className="mb-2">Edit your document in the editor</li>
          <li className="mb-2">Save your changes</li>
          <li className="mb-2">Share your document with others</li>
        </ol>
      </div>
    )
  },
  { 
    index: 2, 
    name: 'features.md', 
    route: '/studio/features', 
    visible: false,
    mode: 'view',
    content: (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Features</h1>
        <p className="mb-4">Studio Space comes with the following features:</p>
        <ul className="list-disc pl-6">
          <li className="mb-2">VS Code-like tab interface</li>
          <li className="mb-2">Markdown support</li>
          <li className="mb-2">Multiple document management</li>
          <li className="mb-2">Customizable themes</li>
        </ul>
      </div>
    )
  },
];

export default function StudioSpace() {
  const { isAuthenticated, initiateGoogleLogin } = useAuth();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [visiblePageIndexes, setVisiblePageIndexes] = useState(
    initialPages.filter(page => page.visible).map(page => page.index)
  );
  const [visiblePages, setVisiblePages] = useState(
    initialPages.filter(page => page.visible)
  );
  const [pages, setPages] = useState(initialPages);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // State to store editor content for each page
  const [editorContents, setEditorContents] = useState<Record<number, string>>({});

  // Initialize editor contents from the initial pages
  useEffect(() => {
    const initialEditorContents: Record<number, string> = {};
    pages.forEach(page => {
      // Create a simple text representation of the content for the editor
      // In a real app, you'd store the actual markdown content
      if (!editorContents[page.index]) {
        initialEditorContents[page.index] = `# ${page.name.replace('.md', '')}\n\nEdit this document in the editor.`;
      }
    });
    
    // Only set new content for pages that don't already have content
    if (Object.keys(initialEditorContents).length > 0) {
      setEditorContents(prev => ({
        ...prev,
        ...initialEditorContents
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to save the current editor content
  const saveEditorContent = () => {
    const selectedPage = pages.find(page => page.index === selectedIndex);
    if (selectedPage && selectedPage.mode === 'edit') {
      console.log('Saving editor content for tab', selectedIndex);
      // Here you would typically save the content to your backend
    }
  };

  // Function to handle tab selection
  const handleTabSelect = (index: number) => {
    // Save current editor content before switching tabs
    saveEditorContent();
    
    setSelectedIndex(index);
  };

  // Function to handle tab close
  const handleTabClose = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Save editor content if the tab being closed is in edit mode
    const pageToClose = pages.find(page => page.index === index);
    if (pageToClose && pageToClose.mode === 'edit') {
      console.log('Saving editor content before closing tab', index);
      // Here you would typically save the content to your backend
    }
    
    // Don't allow closing the last tab
    if (visiblePageIndexes.length <= 1) return;
    
    const newVisiblePageIndexes = visiblePageIndexes.filter(i => i !== index);
    setVisiblePageIndexes(newVisiblePageIndexes);
    
    // Update visible pages
    const newVisiblePages = pages.filter(page => 
      newVisiblePageIndexes.includes(page.index)
    );
    setVisiblePages(newVisiblePages);
    
    // If we're closing the selected tab, select another one
    if (selectedIndex === index) {
      const newSelectedIndex = newVisiblePageIndexes[0];
      setSelectedIndex(newSelectedIndex);
    }
  };

  // Function to toggle between view and edit modes
  const handleModeToggle = (index: number) => {
    const updatedPages = pages.map(page => {
      if (page.index === index) {
        return {
          ...page,
          mode: page.mode === 'view' ? 'edit' as const : 'view' as const
        };
      }
      return page;
    });
    
    setPages(updatedPages);
    
    // Update visible pages
    const newVisiblePages = updatedPages.filter(page => 
      visiblePageIndexes.includes(page.index)
    );
    setVisiblePages(newVisiblePages);
  };

  // Function to add a new tab
  const handleAddTab = () => {
    // Find the first invisible page
    const invisiblePage = pages.find(page => 
      !visiblePageIndexes.includes(page.index)
    );
    
    if (invisiblePage) {
      const newVisiblePageIndexes = [...visiblePageIndexes, invisiblePage.index];
      setVisiblePageIndexes(newVisiblePageIndexes);
      
      // Update visible pages
      const newVisiblePages = pages.filter(page => 
        newVisiblePageIndexes.includes(page.index)
      );
      setVisiblePages(newVisiblePages);
      
      // Select the new tab
      setSelectedIndex(invisiblePage.index);
    }
  };

  // Function to handle editor content update
  const handleEditorUpdate = (index: number, editor: any) => {
    // Get the JSON content from the editor
    const content = editor.getJSON();
    
    // Update the editor contents state
    setEditorContents(prev => ({
      ...prev,
      [index]: content
    }));
    
    console.log('Editor content updated for tab', index);
  };

  // Render the content of the selected tab
  const renderTabContent = () => {
    const selectedPage = pages.find(page => page.index === selectedIndex);
    
    if (!selectedPage) return null;
    
    // If in edit mode, render the Novel editor
    if (selectedPage.mode === 'edit') {
      return (
        <div className="p-4">
          <h2 className="text-xl font-semibold mb-4">Editing: {selectedPage.name}</h2>
          <Editor
            key={`editor-${selectedPage.index}`}
            defaultValue={editorContents[selectedPage.index] || ''}
            disableLocalStorage={true}
            onUpdate={(editor) => handleEditorUpdate(selectedPage.index, editor)}
            className="w-full rounded-md border border-gray-200 min-h-[500px]"
          />
        </div>
      );
    }
    
    // Otherwise, render the regular content in view mode
    return (
      <div className="view-mode">
        {selectedPage.content}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with tabs */}
      <SpaceHeader 
        selectedIndex={selectedIndex}
        visiblePages={visiblePages}
        onTabSelect={handleTabSelect}
        onTabClose={handleTabClose}
        onAddTab={handleAddTab}
        onModeToggle={handleModeToggle}
      />
      
      {/* Content Area */}
      <div className="flex-1 overflow-auto bg-white dark:bg-gray-800">
        {isAuthenticated ? (
          renderTabContent()
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-center mb-8 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-md">
              <h2 className="text-2xl font-bold mb-4">Sign in to access Studio Space</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                You need to sign in to create and manage your content.
              </p>
              
              <button
                onClick={initiateGoogleLogin}
                className="flex items-center gap-2 px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 mx-auto"
              >
                <img src="/google-icon.svg" alt="Google" className="w-5 h-5" />
                <span>Sign in with Google</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 